import { select } from 'redux-saga/effects'
import type { Layer } from '@carto/kepler.gl/layers'

import { RootState } from 'app/state/store'
import { MapDataset } from 'features/maps/types'
import { SchemaField } from 'features/data-explorer/types'
import {
  MapDatasetEnriched,
  PopupEvent,
  PopupFormatSettings,
  PopupSettings,
  TimeSeriesWidget,
  WidgetType
} from 'features/builder/types'
import {
  getColumnNameFromGeoColumn,
  isAsyncPopupSupported,
  isDynamicSpatialIndexDataset,
  shouldLoadAsDynamicTiling
} from './builderSliceUtils'
import { getEnrichedDatasetById } from './keplerGlSlice'
import { getFieldsFromTemplate, isLayerPopupEnabled } from 'features/builder/utils/popupUtils'
import { ArtificialSchemaField } from '../ui/builderConstants'
import { isEqual } from 'lodash'

export const datasetColors = [
  '#7F3C8D',
  '#11A579',
  '#3969AC',
  '#F2B701',
  '#E73F74',
  '#80BA5A',
  '#E68310',
  '#008695',
  '#CF1C90',
  '#f97b72',
  '#4b4b8f',
  '#A5AA99'
]

export function getNewDatasetColor(mapDatasets: MapDataset[], index?: number) {
  for (const c of datasetColors) {
    const isUsedColor = mapDatasets.some((d) => d.color === c)
    if (!isUsedColor) {
      return c
    }
  }
  return datasetColors[(index ?? mapDatasets.length) % datasetColors.length]
}

export function datasetRequiresColumns(dataset: Pick<MapDataset, 'type' | 'format' | 'geoColumn'>): boolean {
  return shouldLoadAsDynamicTiling(dataset) && !isDynamicSpatialIndexDataset(dataset)
}

export function getDatasetDefaultColumns({
  geoColumn,
  schema
}: {
  geoColumn?: string | null
  schema?: SchemaField[]
}): string[] | undefined {
  if (!geoColumn) return undefined

  const defaultGeometryColumn = getColumnNameFromGeoColumn(geoColumn)
  if (!defaultGeometryColumn) {
    // in this case, let's pretend we can't use `columns` feature at all
    return undefined
  }

  if (schema) {
    const columns: string[] = []
    const geoColumnField = schema.find((f) => f.name.toLowerCase() === defaultGeometryColumn.toLowerCase())
    if (geoColumnField) {
      columns.push(geoColumnField.name)
    }
    return columns.sort()
  } else {
    return [defaultGeometryColumn]
  }
}

export function* getDatasetColumnsFromMap(datasetId, validFields?: string[]) {
  try {
    const dataset: MapDatasetEnriched = yield select((state) => getEnrichedDatasetById(state, datasetId))

    if (!dataset || !datasetRequiresColumns(dataset)) {
      return null
    }

    if (!validFields) {
      validFields = dataset?.metadata?.schema?.map((f) => f.name) as string[] | undefined
    }

    const popupSettings = yield select((state) => state.builder.popupSettings)
    const layers = yield select((state) => state.keplerGl?.map?.visState.layers)

    const widgets: TimeSeriesWidget[] = yield select((state: RootState) =>
      state.builder.widgets.filter(
        (w) => w.type === WidgetType.TIMESERIES && w.showControls && w.dataSource === datasetId && w.isValid
      )
    )

    return getDatasetColumns({
      dataset,
      schema: dataset?.metadata?.schema,
      layers,
      popupSettings,
      widgets,
      validFields
    })
  } catch (e: unknown) {
    console.error('getDatasetColumnsFromState', e)
    throw e
  }
}

export function getDatasetColumns({
  layers,
  dataset,
  schema,
  popupSettings,
  widgets,
  validFields
}: {
  layers: Layer[]
  dataset: MapDatasetEnriched
  schema?: SchemaField[]
  popupSettings?: PopupSettings
  widgets?: TimeSeriesWidget[]
  validFields?: string[] | undefined
}): string[] | null {
  const EMPTY_ARRAY: string[] = []
  const datasetId = dataset.id
  const defaultColumns = getDatasetDefaultColumns({ geoColumn: dataset.geoColumn, schema })
  if (!defaultColumns) {
    return null
  }

  const validColumnNames = validFields && new Set(validFields)

  const widgetColumns =
    widgets?.flatMap((widget) => {
      const result: string[] = []
      if (widget.column) {
        result.push(widget.column)
      }
      return result
    }) || EMPTY_ARRAY

  const layerConfigColumns = layers
    .filter((layer) => layer.config.dataId === datasetId)
    .flatMap((layer) => {
      const visualChannels = layer.visualChannels
      const visualChannelFields = Object.values(visualChannels).reduce((acc, channel) => {
        const { field, range, condition } = channel

        if (condition && !condition(layer.config)) {
          return acc
        }

        if (field) {
          const visChannelFieldConfig = layer.config[field]
          if (visChannelFieldConfig) {
            acc.push(visChannelFieldConfig.name)
          }
          if (range) {
            const visChannelRange = layer.config.visConfig[range]
            if (visChannelRange) {
              if (visChannelRange.hexColor && visChannelFieldConfig?.colorColumn) {
                acc.push(visChannelFieldConfig?.colorColumn)
              }
            }
          }
        }

        return acc
      }, [] as string[])

      const getLayerPopupFields = (settings: PopupFormatSettings): string[] => {
        const fields = settings.templateMode
          ? getFieldsFromTemplate(settings.template, schema as SchemaField[])
          : settings.fields
        return fields.map(({ name }) => name)
      }

      const textLabelFields = layer.config.textLabel?.map(({ field }) => field?.id).filter((field) => field) as string[]
      const layerPopupSettings = popupSettings?.layers?.[layer.id]
      const isClickEnabled = isLayerPopupEnabled(PopupEvent.Click, layerPopupSettings)
      const isHoverEnabled = isLayerPopupEnabled(PopupEvent.Hover, layerPopupSettings)

      // For click popups using dynamic spatial index datasets _only_, lazy-load
      // popup fields from Picking Model API using _carto_feature_id lookups.
      let clickFields: string[]

      if (isClickEnabled && isAsyncPopupSupported(dataset)) {
        clickFields = [ArtificialSchemaField.FEATURE_ID]
        // Unlike _carto_point_density, _carto_feature_id is not returned in
        // schema. The difference could be revisited in the future.
        validColumnNames?.add(ArtificialSchemaField.FEATURE_ID)
      } else if (isClickEnabled) {
        clickFields = [
          ArtificialSchemaField.FEATURE_ID,
          ...getLayerPopupFields(layerPopupSettings.click as PopupFormatSettings)
        ]
        validColumnNames?.add(ArtificialSchemaField.FEATURE_ID)
      } else {
        clickFields = EMPTY_ARRAY
      }

      // For hover popups (which are limited to <=5 fields), eagerly load all
      // fields required for the popup.
      let hoverFields: string[]
      if (isHoverEnabled) {
        hoverFields = [
          ArtificialSchemaField.FEATURE_ID,
          ...getLayerPopupFields(layerPopupSettings.hover as PopupFormatSettings)
        ]
        // Unlike _carto_point_density, _carto_feature_id is not returned in
        // schema. The difference could be revisited in the future.
        validColumnNames?.add(ArtificialSchemaField.FEATURE_ID)
      } else {
        hoverFields = EMPTY_ARRAY
      }

      return [...visualChannelFields, ...textLabelFields, ...clickFields, ...hoverFields]
    })

  const requiredColumns = Array.from(new Set([...defaultColumns, ...layerConfigColumns, ...widgetColumns])).sort()
  if (validColumnNames) {
    return requiredColumns.filter((column) => validColumnNames.has(column))
  } else {
    return requiredColumns
  }
}

export function isAggregatedDataset(dataset: MapDatasetEnriched) {
  return !!dataset?.aggregationExp || isDynamicSpatialIndexDataset(dataset)
}

export function isWorkflowDataset(dataset: MapDataset) {
  return Boolean(dataset.sourceWorkflowId)
}

export function isWorkflowDatasetTemp(dataset: MapDataset) {
  return isWorkflowDataset(dataset) && dataset.source ? /.*WORKFLOW_\w{16}_\w{16}_\w*$/i.test(dataset.source) : false
}

export function datasetSchemaEqual(a: SchemaField[] | undefined, b: SchemaField[] | undefined) {
  if (!a || !b) return a === b
  if (a.length !== b.length) return false

  return isEqual(
    a.toSorted((a, b) => a.name.localeCompare(b.name)),
    b.toSorted((a, b) => a.name.localeCompare(b.name))
  )
}
